import { Customer, Organisation, PaginatedResponseMeta, User } from '@/models';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import apiClient from './client/apiClient';

// After the Axios response interceptor unwraps response.data, the resolved value for a
// paginated endpoint is the response body: { data: T[], meta?: PaginatedResponseMeta }.
// A non-paginated endpoint resolves to T[] directly. This guard distinguishes the two.
const isPaginatedOrganisationsResponseShape = (
  value: unknown
): value is { data: Organisation[]; meta?: PaginatedResponseMeta } => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as { data?: unknown };
  return Array.isArray(candidate.data);
};

interface Session {
  access_token: string;
  refresh_token: string;
  recovery_code: string;
  error: string;
  mfa_token: string;
  user_id: string;
  mfa_enrollments: {
    status: string;
    type: string;
    phone_number: string;
  }[];
  expires_in: number;
}

export class UserService {
  async login(params: AxiosRequestConfig = {}): Promise<Session> {
    return await apiClient.post('v1/login', params);
  }

  async refreshToken(params: AxiosRequestConfig = {}): Promise<Session> {
    return await apiClient.post('v1/refresh-token', params);
  }

  async revokeToken(params: AxiosRequestConfig = {}) {
    return await apiClient.post('v1/revoke-token', params);
  }

  async mfaAssociate(params: AxiosRequestConfig = {}): Promise<{
    barcode_uri: string;
    oob_code: string;
    recovery_codes: string[];
  }> {
    return await apiClient.post('v1/mfa-associate', params);
  }

  async mfaChallenge(params: AxiosRequestConfig = {}): Promise<{ oob_code: string; }> {
    return await apiClient.post('v1/mfa-challenge', params);
  }

  async resetMFA(params: AxiosRequestConfig = {}) {
    return await apiClient.post('v1/users/current/reset-mfa', params);
  }

  async agreeToCustomerTermsOfService(customerId: string, params: AxiosRequestConfig = {}) {
    return await apiClient.post(`v1/users/current/customers/${customerId}`, params);
  }

  async updateCurrentUserInCustomer(
    customerId: string, data: { download_patient_data: boolean }, params: AxiosRequestConfig = {}) {
    return await apiClient.put(`v1/users/current/customers/${customerId}`, data, params);
  }

  async getUserCustomers(params: AxiosRequestConfig = {}): Promise<Customer[]> {
    return (await apiClient.get(`v1/users/current/customers`, params)).data;
  }

  async updateAccountSetting(data: Partial<User>, params: AxiosRequestConfig = {}): Promise<AxiosResponse<User>> {
    return await apiClient.patch('v1/users/current/account-setting', data, params);
  }

  async requestResetPasswordEmail(data: { email: string }, params: AxiosRequestConfig = {}) {
    return await apiClient.post('v1/request-reset-password', data, params);
  }

  async setPassword(
    data: { password: string; password_confirmation: string; token: string; email: string },
    params: AxiosRequestConfig = {}
  ) {
    return await apiClient.put('v1/reset-password', data, params);
  }

  async setPasswordAndActivate(
    data: { password: string; password_confirmation: string; token: string },
    params: AxiosRequestConfig = {}
  ) {
    return await apiClient.put('v1/set-password-and-activate', data, params);
  }

  async sendInvite(data: { token: string }): Promise<void> {
    await apiClient.post('v1/send-invite', data);
  }

  async getCurrent(params: AxiosRequestConfig = {}): Promise<User> {
    return (await apiClient.get('v1/me', params)).data;
  }

  async updateCurrent(changes: Partial<User>, params: AxiosRequestConfig = {}): Promise<User> {
    return (await apiClient.patch('v1/me', changes, params)).data;
  }

  async getCurrentOrganisations(params: AxiosRequestConfig = {}): Promise<Organisation[]> {
    const response = await apiClient.get('v1/users/current/organisations', params);

    // The Axios response interceptor unwraps response.data, so `response` is already
    // the response body: either { data: Organisation[], meta?: {...} } (paginated shape) or
    // Organisation[] (non-paginated).
    if (isPaginatedOrganisationsResponseShape(response)) {
      return response.data;
    }

    return response as unknown as Organisation[];
  }

  async getAllCurrentOrganisations(params: AxiosRequestConfig = {}): Promise<Organisation[]> {
    const allOrganisations: Organisation[] = [];
    let currentPage = 1;
    let hasMorePages = true;

    while (hasMorePages) {
      // The Axios response interceptor unwraps response.data, so `response` is already
      // the response body: either { data: Organisation[], meta?: {...} } (paginated shape) or
      // Organisation[] (non-paginated).
      const response = await apiClient.get('v1/users/current/organisations', {
        ...params,
        params: {
          ...params.params,
          page: currentPage,
          per_page: 50
        }
      });

      if (isPaginatedOrganisationsResponseShape(response)) {
        allOrganisations.push(...response.data);

        const meta = (response as { data: Organisation[]; meta?: PaginatedResponseMeta }).meta;
        if (meta && meta.current_page < (meta.last_page ?? meta.current_page)) {
          currentPage++;
        } else {
          hasMorePages = false;
        }
      } else {
        // Non-paginated fallback: response is directly the array
        allOrganisations.push(...(response as unknown as Organisation[]));
        hasMorePages = false;
      }
    }

    return allOrganisations;
  }

  async searchUserByEmail(params: AxiosRequestConfig = {}): Promise<User[]> {
    return (await apiClient.get('v1/user-search-by-email', params)).data;
  }
}
